import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BookOpen,
  MessageSquare,
  CheckSquare,
  TrendingUp,
  Target,
  Clock,
  Search,
  Loader2,
  AlertCircle
} from 'lucide-react'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Avatar } from '@/components/ui/Avatar'
import { StatTile, StatTileTone } from '@/components/ui/StatTile'
import ActivePlanCard from '@/components/plans/ActivePlanCard'
import { useAuth } from '@/contexts/Auth0Context'
import { taskService } from '@/services/taskService'
import { matchingService } from '@/services/matchingService'
import { messageService } from '@/services/messageService'
import { Task } from '@/types/task'
import { Relationship, RelationshipStatus } from '@/types/matching'
import { Conversation } from '@/types/message'
import RateRelationshipModal from '@/components/matching/RateRelationshipModal'
import MeetingRequestModal from '@/components/meetings/MeetingRequestModal'
import { useToast } from '@/contexts/ToastContext'
import { dismissRatingPrompt, findRelationshipNeedingRating } from '@/utils/ratingPrompt'

interface DashboardStats {
  activeTasks: number
  completedTasks: number
  totalTasks: number
  progressPercentage: number
}

const MenteeDashboard: React.FC = () => {
  const { user } = useAuth()
  const { showToast } = useToast()
  const navigate = useNavigate()
  const [stats, setStats] = useState<DashboardStats>({
    activeTasks: 0,
    completedTasks: 0,
    totalTasks: 0,
    progressPercentage: 0
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentTasks, setCurrentTasks] = useState<Task[]>([])
  const [mentor, setMentor] = useState<Relationship['mentor'] | null>(null)
  const [mentorRelationshipId, setMentorRelationshipId] = useState<string | null>(null)
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false)
  const [recentConversations, setRecentConversations] = useState<Conversation[]>([])
  const [ratingRelationship, setRatingRelationship] = useState<Relationship | null>(null)
  const [isRatingSubmitting, setIsRatingSubmitting] = useState(false)

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true)
        setError(null)

        const [taskStatsResult, tasksResponseResult, relationshipsResult, conversationsResult] =
          await Promise.allSettled([
            taskService.getTaskStats(),
            taskService.getMyTasks({ status: ['pending', 'in_progress'], limit: 5 }),
            matchingService.getMyRelationships(),
            messageService.getConversations()
          ])

        for (const [label, result] of [
          ['task stats', taskStatsResult],
          ['tasks', tasksResponseResult],
          ['relationships', relationshipsResult],
          ['conversations', conversationsResult]
        ] as const) {
          if (result.status === 'rejected') {
            console.error(`Failed to load ${label}:`, result.reason)
          }
        }

        const taskStats =
          taskStatsResult.status === 'fulfilled'
            ? taskStatsResult.value
            : { total: 0, pending: 0, inProgress: 0, underReview: 0, completed: 0, overdue: 0, avgProgress: 0 }
        const tasksResponse = tasksResponseResult.status === 'fulfilled' ? tasksResponseResult.value : []
        const relationships = relationshipsResult.status === 'fulfilled' ? relationshipsResult.value : []
        const conversations = conversationsResult.status === 'fulfilled' ? conversationsResult.value : []

        if ([taskStatsResult, tasksResponseResult, relationshipsResult, conversationsResult].every(
          (result) => result.status === 'rejected'
        )) {
          setError('Failed to load dashboard data. Please try again.')
        }

        const activeRelationship = relationships.find(
          (r) => r.status === RelationshipStatus.ACTIVE
        )

        setStats({
          activeTasks: taskStats.pending + taskStats.inProgress,
          completedTasks: taskStats.completed,
          totalTasks: taskStats.total,
          progressPercentage: Math.round(taskStats.avgProgress)
        })

        setCurrentTasks(tasksResponse.slice(0, 3))
        setMentor(activeRelationship?.mentor || null)
        setMentorRelationshipId(activeRelationship?.id || null)
        setRecentConversations(conversations.slice(0, 3))

        if (user) {
          setRatingRelationship(findRelationshipNeedingRating(relationships, user.id, 'mentee'))
        }
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err)
        setError('Failed to load dashboard data. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  const handleCloseRatingPrompt = () => {
    if (user && ratingRelationship) {
      dismissRatingPrompt(user.id, ratingRelationship.id)
    }
    setRatingRelationship(null)
  }

  const handleSubmitRating = async (rating: number, review?: string) => {
    if (!ratingRelationship) return

    try {
      setIsRatingSubmitting(true)
      await matchingService.rateRelationship(ratingRelationship.id, { rating, review })
      showToast({ type: 'success', message: 'Rating submitted!' })
      setRatingRelationship(null)
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to submit rating'
      })
    } finally {
      setIsRatingSubmitting(false)
    }
  }

  const statCards: { title: string; value: string | number; icon: typeof CheckSquare; tone: StatTileTone }[] = [
    {
      title: 'Active Tasks',
      value: stats.activeTasks,
      icon: CheckSquare,
      tone: 'primary'
    },
    {
      title: 'Completed Tasks',
      value: stats.completedTasks,
      icon: TrendingUp,
      tone: 'success'
    },
    {
      title: 'Total Tasks',
      value: stats.totalTasks,
      icon: Target,
      tone: 'accent'
    },
    {
      title: 'Avg Progress',
      value: `${stats.progressPercentage}%`,
      icon: TrendingUp,
      tone: 'warning'
    }
  ]

  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  }

  const formatDueDate = (dateString?: string): string => {
    if (!dateString) return 'No due date'
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = date.getTime() - now.getTime()
    const diffDays = Math.ceil(diffMs / 86400000)

    if (diffDays < 0) return 'Overdue'
    if (diffDays === 0) return 'Due today'
    if (diffDays === 1) return 'Due tomorrow'
    if (diffDays <= 7) return `Due in ${diffDays} days`
    return `Due in ${Math.ceil(diffDays / 7)} week${Math.ceil(diffDays / 7) > 1 ? 's' : ''}`
  }

  const getTaskColor = (progress: number): { bg: string; border: string; bar: string } => {
    if (progress >= 75) return { bg: 'bg-green-50', border: 'border-green-200', bar: 'bg-green-600' }
    if (progress >= 40) return { bg: 'bg-amber-50', border: 'border-amber-200', bar: 'bg-amber-600' }
    return { bg: 'bg-primary-50', border: 'border-primary-200', bar: 'bg-primary-600' }
  }

  const getTaskButton = (progress: number): { label: string; variant: 'primary' | 'outline' } => {
    if (progress >= 90) return { label: 'Submit', variant: 'primary' }
    if (progress > 0) return { label: 'Continue', variant: 'primary' }
    return { label: 'Start', variant: 'outline' }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4 text-center">
          <AlertCircle className="w-12 h-12 text-red-500" />
          <p className="text-gray-900 font-medium">{error}</p>
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-display text-gray-900">
            Good to see you, {user?.firstName}
          </h1>
          <p className="mt-2 text-gray-600">
            Continue your learning journey
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat) => (
            <StatTile key={stat.title} title={stat.title} value={stat.value} icon={stat.icon} tone={stat.tone} />
          ))}
        </div>

        {/* Active mentorship plan */}
        <div className="mb-8">
          <ActivePlanCard />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Current Tasks */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <h2 className="text-lg font-bold text-gray-900">
                Current Tasks
              </h2>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                {currentTasks.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No active tasks. Ask your mentor to assign new tasks!
                  </p>
                ) : (
                  currentTasks.map((task) => {
                    const colors = getTaskColor(task.progressPercentage)
                    const button = getTaskButton(task.progressPercentage)
                    return (
                      <div
                        key={task.id}
                        className={`flex items-center justify-between gap-4 p-5 ${colors.bg} rounded-2xl`}
                      >
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900">{task.title}</h3>
                          <div className="mt-3">
                            <div className="bg-white/60 rounded-full h-2.5">
                              <div
                                className={`${colors.bar} h-2.5 rounded-full transition-all`}
                                style={{ width: `${task.progressPercentage}%` }}
                              ></div>
                            </div>
                            <div className="flex justify-between text-xs text-gray-500 mt-1.5">
                              <span>{task.progressPercentage}% complete</span>
                              <span>{formatDueDate(task.dueDate)}</span>
                            </div>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant={button.variant}
                          onClick={() => navigate(`/tasks/${task.id}`)}
                        >
                          {button.label}
                        </Button>
                      </div>
                    )
                  })
                )}
              </div>

              <div className="mt-6">
                <Button variant="outline" className="w-full" onClick={() => navigate('/tasks')}>
                  <CheckSquare className="w-4 h-4 mr-2" />
                  View All Tasks
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* Mentor & Activities */}
          <div className="space-y-6">
            {/* Current Mentor */}
            <Card>
              <CardHeader>
                <h2 className="text-lg font-bold text-gray-900">
                  Your Mentor
                </h2>
              </CardHeader>
              <CardBody>
                {mentor ? (
                  <>
                    <div className="flex items-center space-x-3 mb-4">
                      <Avatar
                        src={mentor.profile?.profilePicture}
                        name={`${mentor.firstName} ${mentor.lastName}`}
                        size="md"
                      />
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {mentor.firstName} {mentor.lastName}
                        </h3>
                        {mentor.profile?.expertise && mentor.profile.expertise.length > 0 && (
                          <p className="text-sm text-gray-500">
                            {mentor.profile.expertise.slice(0, 2).join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Button
                        variant="primary"
                        size="sm"
                        className="w-full"
                        onClick={() => navigate(`/messages?relationshipId=${mentorRelationshipId}`)}
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Send Message
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => setIsMeetingModalOpen(true)}
                      >
                        <Clock className="w-4 h-4 mr-2" />
                        Schedule Meeting
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500 mb-4">
                      You don't have a mentor yet
                    </p>
                    <Button variant="primary" size="sm" onClick={() => navigate('/find-mentors')}>
                      <Search className="w-4 h-4 mr-2" />
                      Find a Mentor
                    </Button>
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Recent Activities */}
            <Card>
              <CardHeader>
                <h2 className="text-lg font-bold text-gray-900">
                  Recent Activities
                </h2>
              </CardHeader>
              <CardBody>
                <div className="space-y-3">
                  {recentConversations.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No recent activities
                    </p>
                  ) : (
                    recentConversations.map((conv) => (
                      <div key={conv.relationshipId} className="flex items-start space-x-3">
                        <div className="bg-accent-100 p-2 rounded-full">
                          <MessageSquare className="w-4 h-4 text-accent-700" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {conv.unreadCount > 0
                              ? `${conv.unreadCount} new message${conv.unreadCount > 1 ? 's' : ''} from ${conv.participant.firstName}`
                              : `Message from ${conv.participant.firstName}`}
                          </p>
                          {conv.lastMessage && (
                            <p className="text-sm text-gray-500">
                              {formatRelativeTime(conv.lastMessage.createdAt)}
                            </p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Button variant="primary" className="justify-center" onClick={() => navigate('/find-mentors')}>
              <Search className="w-4 h-4 mr-2" />
              Find New Mentor
            </Button>
            <Button
              variant="outline"
              className="justify-center"
              onClick={() =>
                navigate(mentorRelationshipId ? `/messages?relationshipId=${mentorRelationshipId}` : '/messages')
              }
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Message Mentor
            </Button>
            <Button variant="outline" className="justify-center" onClick={() => navigate('/resources')}>
              <BookOpen className="w-4 h-4 mr-2" />
              Browse Resources
            </Button>
          </div>
        </div>
      </div>

      {mentor && (
        <MeetingRequestModal
          isOpen={isMeetingModalOpen}
          onClose={() => setIsMeetingModalOpen(false)}
          onMeetingRequested={() => setIsMeetingModalOpen(false)}
          preselectedRecipientId={mentor.id}
        />
      )}

      {/* Rating prompt for a completed relationship */}
      {ratingRelationship && (
        <RateRelationshipModal
          relationship={ratingRelationship}
          userRole="mentee"
          isOpen={!!ratingRelationship}
          onClose={handleCloseRatingPrompt}
          onSubmit={handleSubmitRating}
          isSubmitting={isRatingSubmitting}
        />
      )}
    </div>
  )
}

export default MenteeDashboard